import {
  Breadcrumb,
  Modal,
  Tag,
  Toast,
  Popconfirm,
  TextArea,
} from '@douyinfe/semi-ui';
import Text from '@douyinfe/semi-ui/lib/es/typography/text';
import { Link, useHistory } from '@jupiter/plugin-runtime/router';
import dayjs from 'dayjs';
import { useRequest } from '@byted/hooks';
import cls from 'classnames';
import { forumpost } from '@/typings/open.platform.forum_bff';
import { useScope } from '@/hooks/useScope';
import { useTopAndGood } from '@/hooks/useTop';
import useBiz from '@/context/useBiz';
import { useUserInfo } from '@/hooks/useUserInfo';
import { forumPostService } from '@/api/forumPost';
import { RemovePostRequest } from '@/typings/open.platform.forum_bff/namespaces/forumpost';
import { toastError } from '@/utils/toastError';
import {
  usePostNonSelfVisible,
  usePostSelfVisible,
} from '@/hooks/useSelfVisible';
import { useBulletinId } from '@/hooks/useBulletinId';
import { Scope } from '@/typings/fe';

const PostHeader: React.FC<{
  info?: forumpost.Post;
  refetch: () => Promise<any>;
}> = ({ info, refetch }) => {
  const scope = useScope();
  const history = useHistory();
  const { ifAdmin, User } = useUserInfo();
  const isOwner = info?.UserId && info.UserId === User?.Id;
  const biz = useBiz(s => s.biz);
  const { toggleGood, toggleTop } = useTopAndGood();
  const { run: deletePost } = useRequest(
    async (param: RemovePostRequest) => {
      const res = await forumPostService.removePost(param);
      if (res.error) {
        throw new Error(res.message);
      }
      return res.data;
    },
    {
      onSuccess() {
        Toast.success({
          content: '删除成功, 3 秒后跳转到首页',
        });
        setTimeout(() => {
          history.push('/');
        }, 3000);
      },
      onError(err) {
        toastError(err);
      },
      auto: false,
    },
  );

  const { run: selfVisiblePost } = usePostSelfVisible();
  const { run: nonSelfVisiblePost } = usePostNonSelfVisible();

  const deletePostConfirm = () => {
    if (isOwner) {
      Modal.confirm({
        title: '确认删除?',
        async onOk() {
          await deletePost({
            PostId: info.Id,
            Reason: 'self',
            Uid: '',
            Biz: biz,
          });
        },
      });
    } else if (ifAdmin) {
      let reason = '';
      const ok = async () => {
        if (!reason || !info?.Id) {
          throw new Error();
        }
        await deletePost({
          PostId: info.Id,
          Reason: reason,
          Uid: '',
          Biz: biz,
        });
      };
      const ins = Modal.confirm({
        title: '请输入原因',
        okButtonProps: {
          disabled: !reason,
        },
        content: (
          <TextArea
            placeholder="支持 Markdown"
            maxLength={140}
            maxCount={140}
            showCounter={true}
            onChange={v => {
              reason = v;
              ins.update({
                okButtonProps: {
                  disabled: !reason,
                },
                type: 'confirm',
              });
            }}
          />
        ),
        onOk: ok,
      });
    }
  };

  const selfVisibleConfirm = () => {
    let reason = '';
    const ok = async () => {
      if (!info?.Id) {
        throw new Error('postid 不能为空');
      }
      try {
        if (info?.Visible) {
          await selfVisiblePost({
            PostId: info?.Id,
            Reason: reason,
            Uid: '',
            Biz: biz,
          });
        } else {
          await nonSelfVisiblePost({
            PostId: info.Id,
            Reason: reason,
            Uid: '',
            Biz: biz,
          });
        }
        await refetch();
        Toast.success('操作成功');
      } catch (e) {
        toastError(e);
      }
    };
    const ins = Modal.confirm({
      title: info?.Visible ? '确认自见?' : '确认取消自见?',
      okButtonProps: {
        disabled: !reason,
      },
      content: (
        <TextArea
          placeholder="请输入原因"
          maxLength={140}
          maxCount={140}
          showCounter={true}
          onChange={v => {
            reason = v;
            ins.update({
              okButtonProps: {
                disabled: !reason,
              },
              type: 'confirm',
            });
          }}
        />
      ),
      onOk: ok,
    });
  };

  const { bulletinId } = useBulletinId(info?.Scope as Scope);

  return (
    <div className=" mb-[42px]">
      <div className="flex justify-between mb-[36px]">
        <Breadcrumb separator={'>'} className="text-base leading-6">
          <Breadcrumb.Item>
            <Link to={`/${scope}`}>首页</Link>
          </Breadcrumb.Item>
          {info?.Type === 'bulletin' && (
            <Breadcrumb.Item>
              <Link
                to={`/${info?.Scope ?? ''}/billboard?title=公告&id=${
                  bulletinId ?? ''
                }`}>
                公告
              </Link>
            </Breadcrumb.Item>
          )}
          <Breadcrumb.Item>
            <Text ellipsis={true} className="max-w-[18em]">
              <span className="font-normal text-semi-color-text-0">
                {info?.Title ?? ''}
              </span>
            </Text>
          </Breadcrumb.Item>
        </Breadcrumb>
        {(ifAdmin || isOwner) && (
          <Text type="tertiary" className="space-x-2">
            <Link
              to={`/${scope}/post/${info?.Id ?? ''}/edit`}
              className="cursor-pointer text-semi-color-text-2 hover:text-semi-color-text-0">
              [编辑]
            </Link>
            <span
              className="cursor-pointer text-semi-color-text-2 hover:text-semi-color-text-0"
              onClick={() => {
                deletePostConfirm();
              }}>
              [删除]
            </span>
            {ifAdmin && (
              <span
                className="cursor-pointer text-semi-color-text-2 hover:text-semi-color-text-0"
                onClick={() => {
                  selfVisibleConfirm();
                }}>
                {info?.Visible ? '[自见]' : '[取消自见]'}
              </span>
            )}
            {ifAdmin ? (
              <>
                <Popconfirm
                  title={info?.IsTop ? '确认取消置顶吗？' : '确认置顶吗？'}
                  onConfirm={async () => {
                    await toggleTop(info?.Id ?? '', biz);
                    await refetch();
                  }}>
                  <Tag
                    color="red"
                    className={cls(
                      'cursor-pointer hover:opacity-100 duration-300',
                      info?.IsTop ? '' : 'opacity-30',
                    )}>
                    顶
                  </Tag>
                </Popconfirm>
                <Popconfirm
                  title={info?.IsGood ? '确认取消加精吗？' : '确认加精吗？'}
                  onConfirm={async () => {
                    await toggleGood(info?.Id ?? '', biz);
                    await refetch();
                  }}>
                  <Tag
                    color="green"
                    className={cls(
                      'cursor-pointer hover:opacity-100 duration-300',
                      info?.IsGood ? '' : 'opacity-30',
                    )}>
                    精
                  </Tag>
                </Popconfirm>
              </>
            ) : (
              <>
                {info?.IsTop && <Tag color="red">顶</Tag>}
                {info?.IsGood && <Tag color="green">精</Tag>}
              </>
            )}
          </Text>
        )}
      </div>
      <div className=" mb-4 flex text-2xl">
        {!info?.Visible && ifAdmin && (
          <div className="flex mr-2 items-center h-8">
            <Tag className="mr-2" size="large" color="red">
              自见
            </Tag>
          </div>
        )}
        <span className="font-medium break-all">{info?.Title ?? ''}</span>
      </div>
      <Text type="tertiary" className="space-x-3">
        <span>{info?.ViewCount ?? 0} 浏览</span>
        <span>{dayjs(info?.CreatedAt).format('YYYY年MM月DD日')}</span>
        <span>作者:</span>
        <span
          className="cursor-pointer"
          onClick={() => {
            history.push(`/user/${info?.UserId ?? ''}`);
          }}>
          {info?.UserInfo?.Name ?? '--'}
        </span>
      </Text>
    </div>
  );
};

export default PostHeader;
